import type { KeyboardEvent, ReactNode } from "react";
import UserAvatar from "./UserAvatar";
import "./ResponsiveRankingTable.css";

export type ResponsiveRankingColumn<T> = {
  id: string;
  header: ReactNode;
  mobileLabel: ReactNode;
  render: (row: T, index: number) => ReactNode;
  cellClassName?: string;
};

export type ResponsiveRankingRow<T> = {
  id: string;
  rank: number;
  title: ReactNode;
  data: T;
  avatars?: Array<{
    name: string;
    avatarUrl?: string;
    sex?: "male" | "female" | "other" | "";
    profileHref?: string;
  }>;
  avatarName?: string;
  avatarUrl?: string;
  profileHref?: string;
  summaryValue?: ReactNode;
  rowClassName?: string;
  mobileItemClassName?: string;
};

type ResponsiveRankingTableProps<T> = {
  ariaLabel: string;
  rankHeader: ReactNode;
  titleHeader: ReactNode;
  columns: Array<ResponsiveRankingColumn<T>>;
  rows: Array<ResponsiveRankingRow<T>>;
  emptyMessage?: string;
  emptyState?: ReactNode;
  className?: string;
  isRanking?: boolean;
  onRowClick?: (row: T, index: number) => void;
  getRowActionLabel?: (row: T, index: number) => string;
};

function cx(...values: Array<string | undefined | null | false>) {
  return values.filter(Boolean).join(" ");
}

function rankTone(rank: number) {
  if (rank === 1) return "gold";
  if (rank === 2) return "silver";
  if (rank === 3) return "bronze";
  return "neutral";
}

function rankLabel(rank: number) {
  if (rank === 1) return "🥇";
  if (rank === 2) return "🥈";
  if (rank === 3) return "🥉";
  return rank;
}

function RankBadge({ rank }: { rank: number }) {
  return (
    <span
      className={cx(
        "responsive-ranking-table-rank",
        `responsive-ranking-table-rank--${rankTone(rank)}`,
      )}
    >
      {rankLabel(rank)}
    </span>
  );
}

function AthleteTitle<T>({ row }: { row: ResponsiveRankingRow<T> }) {
  const renderAvatarLink = (avatar: {
    name: string;
    avatarUrl?: string;
    sex?: "male" | "female" | "other" | "";
    profileHref?: string;
  }) => {
    const avatarNode = (
      <UserAvatar
        name={avatar.name}
        sex={avatar.sex}
        avatarUrl={avatar.avatarUrl}
        size={60}
        className="responsive-ranking-table-avatar"
      />
    );
    const profileHref = avatar.profileHref || row.profileHref;
    if (!profileHref) return avatarNode;
    return (
      <a
        className="responsive-ranking-table-avatar-link"
        href={profileHref}
        onClick={(event) => event.stopPropagation()}
      >
        {avatarNode}
      </a>
    );
  };
  const renderNameLink = (name: ReactNode, profileHref?: string) => {
    if (!profileHref) return name;
    return (
      <a
        className="responsive-ranking-table-name-link"
        href={profileHref}
        onClick={(event) => event.stopPropagation()}
      >
        {name}
      </a>
    );
  };

  if (row.avatars?.length) {
    return (
      <span
        className={cx(
          "responsive-ranking-table-athlete",
          row.avatars.length > 1 && "is-pair",
        )}
      >
        {row.avatars.map((avatar, index) => (
          <span
            key={`${row.id}-avatar-${index}`}
            className="responsive-ranking-table-athlete-member"
          >
            {row.avatars.length === 1 ? renderAvatarLink(avatar) : (
              renderAvatarLink(avatar)
            )}
            <span className="responsive-ranking-table-name">
              {row.avatars?.length === 1
                ? typeof row.title === "string"
                  ? renderNameLink(row.title, avatar.profileHref || row.profileHref)
                  : row.title
                : renderNameLink(avatar.name, avatar.profileHref)}
            </span>
          </span>
        ))}
      </span>
    );
  }

  const avatarName =
    String(row.avatarName || "").trim() ||
    (typeof row.title === "string" ? row.title : "");

  return (
    <span className="responsive-ranking-table-athlete">
      {renderAvatarLink({ name: avatarName, avatarUrl: row.avatarUrl })}
      <span className="responsive-ranking-table-name">
        {typeof row.title === "string"
          ? renderNameLink(row.title, row.profileHref)
          : row.title}
      </span>
    </span>
  );
}

export default function ResponsiveRankingTable<T>({
  ariaLabel,
  rankHeader,
  titleHeader,
  columns,
  rows,
  emptyMessage,
  emptyState,
  className,
  isRanking = false,
  onRowClick,
  getRowActionLabel,
}: ResponsiveRankingTableProps<T>) {
  const handleRowKeyDown = (
    event: KeyboardEvent,
    row: ResponsiveRankingRow<T>,
    index: number,
  ) => {
    if (!onRowClick) return;
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    onRowClick(row.data, index);
  };

  return (
    <div className={cx("responsive-ranking-table", className)}>
      <div className="responsive-ranking-table-mobile" aria-label={ariaLabel}>
        {rows.map((row, index) => (
          <details
            key={row.id}
            className={cx(
              "responsive-ranking-table-item",
              `is-rank-${Math.min(Math.max(row.rank, 1), 4)}`,
              row.mobileItemClassName,
            )}
          >
            <summary
              className="responsive-ranking-table-summary"
              role={onRowClick ? "button" : undefined}
              aria-label={
                onRowClick && getRowActionLabel
                  ? getRowActionLabel(row.data, index)
                  : undefined
              }
              onClick={
                onRowClick
                  ? (event) => {
                      event.preventDefault();
                      onRowClick(row.data, index);
                    }
                  : undefined
              }
              onKeyDown={(event) => handleRowKeyDown(event, row, index)}
            >
              <span className="responsive-ranking-table-title">
                <RankBadge rank={row.rank} />
                <AthleteTitle row={row} />
              </span>
              <span className="responsive-ranking-table-summary-meta">
                {row.summaryValue !== undefined && (
                  <span className="responsive-ranking-table-summary-value">
                    {row.summaryValue}
                  </span>
                )}
                {!isRanking && (
                  <span
                    className="responsive-ranking-table-chevron"
                    aria-hidden="true"
                  />
                )}
              </span>
            </summary>
            {!isRanking && (
              <div className="responsive-ranking-table-body">
                {columns.map((column) => (
                  <div
                    key={`mobile-${row.id}-${column.id}`}
                    className="responsive-ranking-table-stat"
                  >
                    <span className="responsive-ranking-table-stat-label">
                      {column.mobileLabel}
                    </span>
                    <span
                      className={cx(
                        "responsive-ranking-table-stat-value",
                        column.cellClassName,
                      )}
                    >
                      {column.render(row.data, index)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </details>
        ))}
        {rows.length === 0 && emptyState}
        {rows.length === 0 && !emptyState && emptyMessage && (
          <p className="status-message">{emptyMessage}</p>
        )}
      </div>

      <div
        className="table-scroll responsive-ranking-table-desktop"
        aria-label={ariaLabel}
      >
        <table className="ranking responsive-ranking-table-grid">
          <thead>
            <tr>
              <th className="responsive-ranking-table-rank-col">
                {rankHeader}
              </th>
              <th className="responsive-ranking-table-name-col">
                {titleHeader}
              </th>
              {columns.map((column) => (
                <th key={`desktop-header-${column.id}`}>{column.header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr
                key={row.id}
                className={cx(
                  `is-rank-${Math.min(Math.max(row.rank, 1), 4)}`,
                  onRowClick && "is-clickable",
                  row.rowClassName,
                )}
                role={onRowClick ? "button" : undefined}
                tabIndex={onRowClick ? 0 : undefined}
                aria-label={
                  onRowClick && getRowActionLabel
                    ? getRowActionLabel(row.data, index)
                    : undefined
                }
                onClick={
                  onRowClick ? () => onRowClick(row.data, index) : undefined
                }
                onKeyDown={(event) => handleRowKeyDown(event, row, index)}
              >
                <td className="responsive-ranking-table-rank-cell">
                  <RankBadge rank={row.rank} />
                </td>
                <td className="responsive-ranking-table-name-cell">
                  <AthleteTitle row={row} />
                </td>
                {columns.map((column) => (
                  <td
                    key={`desktop-row-${row.id}-${column.id}`}
                    className={column.cellClassName}
                  >
                    {column.render(row.data, index)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && emptyState}
        {rows.length === 0 && !emptyState && emptyMessage && (
          <p className="status-message">{emptyMessage}</p>
        )}
      </div>
    </div>
  );
}
